import { useCallback } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { useChatContext } from '@common/Chat/ChatContext'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import {
  filterLeadProviderChatTabsByAccess,
  isIntegrationCrmAccessible,
} from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import {
  getLeadProviderChatTabs,
  getLeadVkUpdatedAt,
  getProviderUnreadCount,
} from '@pages/Admin/CRM/utils/leadFields'
import { getCrmIntegrationChatAccessDeniedAlert } from '@pages/Admin/CRM/components/integrations/common/lib/crmIntegrationChatAccessDeniedAlert'
import {
  useIntegrationChatDock,
  type IntegrationChatDockOpenOptions,
} from './integrationChatDockHooks'

export type OpenLeadIntegrationChatDockOptions = Omit<
  IntegrationChatDockOpenOptions,
  'initialUnreadCount' | 'leadVkUpdatedAt'
> & {
  conversationId?: number | null
  provider?: CrmChatProviderKey
}

export const useOpenLeadIntegrationChatDock = () => {
  const { openDock } = useIntegrationChatDock()
  const chatContext = useChatContext()
  const addAlert = useAddAlert()
  const { connectedIntegrations, isLoading: integrationsLoading } =
    useCrmIntegrationChatAvailability()

  return useCallback(
    (
      leadId: number,
      leadSource?: unknown,
      sessionOptions?: OpenLeadIntegrationChatDockOptions,
    ) => {
      chatContext?.changePage('default')
      const provider = sessionOptions?.provider ?? 'vkontakte'

      const sessionCommunityTabs = sessionOptions?.communityTabs
      const allTabs =
        Array.isArray(sessionCommunityTabs) && sessionCommunityTabs.length > 0
          ? sessionCommunityTabs
          : getLeadProviderChatTabs(leadSource, provider)

      const skipAccessFilter =
        integrationsLoading || connectedIntegrations.length === 0

      const accessibleTabs = skipAccessFilter
        ? allTabs
        : filterLeadProviderChatTabsByAccess(allTabs, connectedIntegrations)

      const requestedConversationId =
        sessionOptions?.conversationId != null &&
        Number.isFinite(Number(sessionOptions.conversationId))
          ? Number(sessionOptions.conversationId)
          : null

      /**
       * Явный conversationId (кнопка поля лида/родителя):
       * доступ как у кнопки поля — deny только если integration найдена и
       * crm_access.is_accessible === false. Раньше `?? false` резал неизвестные id
       * и conversation родителя вне lead.integrations → ложный «Нет доступа» без запросов.
       */
      if (requestedConversationId != null && !skipAccessFilter) {
        const matchedTab = allTabs.find(
          (tab) => Number(tab.conversation_id) === requestedConversationId,
        )
        if (matchedTab) {
          const integrationId = Number(matchedTab.integration_id)
          const integrationItem = connectedIntegrations.find(
            (item) => Number(item.id) === integrationId,
          )
          if (
            integrationItem != null &&
            !isIntegrationCrmAccessible(integrationItem)
          ) {
            addAlert({
              type: 'error',
              title: 'Нет доступа',
              text: getCrmIntegrationChatAccessDeniedAlert(provider),
            })
            return
          }
        }
      } else if (
        requestedConversationId == null &&
        !skipAccessFilter &&
        accessibleTabs.length === 0 &&
        allTabs.length > 0
      ) {
        addAlert({
          type: 'error',
          title: 'Нет доступа',
          text: getCrmIntegrationChatAccessDeniedAlert(provider),
        })
        return
      }

      const conversationId =
        requestedConversationId != null
          ? requestedConversationId
          : (() => {
              const firstTab = accessibleTabs[0] ?? allTabs[0]
              return firstTab ? Number(firstTab.conversation_id) : null
            })()

      const communityTabsForDock =
        Array.isArray(sessionCommunityTabs) && sessionCommunityTabs.length > 0
          ? sessionCommunityTabs
          : accessibleTabs.length > 0
            ? accessibleTabs
            : allTabs

      openDock(leadId, {
        ...sessionOptions,
        initialUnreadCount: getProviderUnreadCount(leadSource, provider),
        leadVkUpdatedAt:
          provider === 'vkontakte'
            ? getLeadVkUpdatedAt(leadSource) ?? null
            : null,
        provider,
        conversationId,
        communityTabs: communityTabsForDock,
      })
    },
    [
      addAlert,
      chatContext,
      connectedIntegrations,
      integrationsLoading,
      openDock,
    ],
  )
}
